import {
  customInstanceWithHeader,
  type ResponseWithHeaders,
} from '@fukuri-admins/apis/client/base/axiosInstance';
import {
  getGetApiV1ThreeLetterCodePartnerAdminsClientsKey,
  getGetApiV1ThreeLetterCodePartnerAdminsClientsUrl,
} from '@fukuri-admins/apis/client/gen/endpoints';
import type {
  GetApiV1ThreeLetterCodePartnerAdminsClients200One,
  GetApiV1ThreeLetterCodePartnerAdminsClientsParams,
} from '@fukuri-admins/apis/client/gen/model';
import { useFetch } from '@fukuri-admins/hooks/common';
import type { KeyedMutator } from 'swr';

const fetcher = (
  threeLetterCode: string,
  apiParams: GetApiV1ThreeLetterCodePartnerAdminsClientsParams
) =>
  customInstanceWithHeader<GetApiV1ThreeLetterCodePartnerAdminsClients200One>(
    getGetApiV1ThreeLetterCodePartnerAdminsClientsUrl(threeLetterCode, apiParams),
    { method: 'GET' }
  );

type Args = {
  /** パートナーの 3 文字コード */
  threeLetterCode: string;
  /** 検索フィルター */
  filter?: {
    /** 企業名（部分一致） */
    name?: string;
    /** 与信状態（'0': 未完了, '1': 完了済）。未指定で絞り込みなし */
    creditGranted?: '0' | '1';
    /** 有効状態（常に '1' を指定すること） */
    enabled?: '1';
  };
  /** ページ番号（1始まりの数値） */
  page?: number;
};

type IUseGetPartnerAdminsClients = (args: Args) => {
  data: GetApiV1ThreeLetterCodePartnerAdminsClients200One;
  totalPage: number;
  totalCount: number;
  mutate: KeyedMutator<ResponseWithHeaders<GetApiV1ThreeLetterCodePartnerAdminsClients200One>>;
};

/**
 * パートナー管理者向け利用企業一覧取得フック
 *
 * - 企業名の部分一致検索・ページネーション対応
 * - total-pages はレスポンスヘッダーから取得して返す
 */
export const useGetPartnerAdminsClients: IUseGetPartnerAdminsClients = ({
  threeLetterCode,
  filter,
  page,
}) => {
  const apiParams: GetApiV1ThreeLetterCodePartnerAdminsClientsParams = {
    ...(filter?.name && { name: filter.name }),
    ...(filter?.creditGranted !== undefined && { credit_granted: Number(filter.creditGranted) }),
    ...(filter?.enabled !== undefined && { enabled: Number(filter.enabled) }),
    ...(page !== undefined && { page }),
  };

  const { data, mutate } = useFetch<
    ResponseWithHeaders<GetApiV1ThreeLetterCodePartnerAdminsClients200One>
  >(
    getGetApiV1ThreeLetterCodePartnerAdminsClientsKey(threeLetterCode, apiParams),
    () => fetcher(threeLetterCode, apiParams),
    { suspense: true }
  );

  const totalPage = Number(data.headers['total-pages']) || 0;
  const totalCount = Number(data.headers['total-count']) || 0;

  return {
    data: data.data,
    totalPage,
    totalCount,
    mutate,
  };
};
